import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  InputField,
  Textarea,
  RadioGroup,
  RadioGroupItem,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DatePicker,
} from "@jem-open/jem-ui";
import { Paperclip, Send, X } from "lucide-react";
import axios from "axios";
import AffiliatedCompany from "../services/model/AffiliatedCompany";
import McmAnnouncement, { McmAnnouncementType } from "../services/model/McmAnnouncement";
import { AnnouncementExtraFields } from "../../announcements/services/model/Announcement";
import McmSegment from "../services/model/McmSegment";
import Segment from "../../segments/services/model/Segment";
import CompanySegmentPicker from "./CompanySegmentPicker";
import { useEstimateAudienceApiService } from "../services/api/McmAnnouncementMutationApiService";
import CreateMcmAnnouncementRequest from "../services/request/CreateMcmAnnouncementRequest";
import { getDisplayStatus } from "../services/model/McmAnnouncementUtils";
import useSecureAnnouncementUploadApiService from "../../announcements/services/api/SecureAnnouncementUploadApiService";
import {
  getMediaType,
  ACCEPT_EXTENSIONS,
  ANNOUNCEMENT_IMAGE_FILE_EXTENSION,
  ANNOUNCEMENT_ACCEPT_IMAGE_FILE_SIZE,
  ANNOUNCEMENT_ACCEPT_FILE_SIZE,
} from "../../announcements/constants/MediaTypes";
import { getFileExtension } from "../../../utils/File";

const extractFilename = (url: string): string => {
  const path = url.split("/").pop()?.split("?")[0] || "Attached file";
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
};

const stripQueryParams = (url: string): string => {
  const idx = url.indexOf("?");
  return idx >= 0 ? url.substring(0, idx) : url;
};

type ComposeFormProps = {
  companies: AffiliatedCompany[];
  segments: McmSegment[];
  ownerCompanyId?: number;
  ownerCompanyName?: string;
  ownerSegments?: Segment[];
  announcementTypes: McmAnnouncementType[];
  editingAnnouncement: McmAnnouncement | null;
  isSaving?: boolean;
  isPublishing?: boolean;
  onGoBack: () => void;
  onSaveDraft: (request: CreateMcmAnnouncementRequest) => void;
  onPublish: (request: CreateMcmAnnouncementRequest) => void;
  onCloseAnnouncement: () => void;
};

const ComposeForm: React.FC<ComposeFormProps> = ({
  companies,
  segments,
  ownerCompanyId,
  ownerCompanyName,
  ownerSegments,
  announcementTypes,
  editingAnnouncement,
  isSaving,
  isPublishing,
  onGoBack,
  onSaveDraft,
  onPublish,
  onCloseAnnouncement,
}) => {
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>(() => {
    const ids = editingAnnouncement?.target_employers?.map((t) => t.id) || [];
    // When editing a draft with owner segments, the backend doesn't include the
    // owner in target_employers. Re-add it so the picker shows the owner checked
    // and the cleanup effect doesn't clear the owner segment selections.
    if (ownerCompanyId != null && editingAnnouncement?.owner_segments?.length) {
      return ids.includes(ownerCompanyId) ? ids : [...ids, ownerCompanyId];
    }
    return ids;
  });
  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>(
    editingAnnouncement?.segments?.map((s) => s.id) || []
  );
  const [selectedOwnerSegmentIds, setSelectedOwnerSegmentIds] = useState<number[]>(
    editingAnnouncement?.owner_segments?.map((s) => s.id) || []
  );
  const [title, setTitle] = useState(editingAnnouncement?.title || "");
  const [content, setContent] = useState(editingAnnouncement?.content || "");
  const [announcementTypeId, setAnnouncementTypeId] = useState<string>(
    editingAnnouncement?.announcement_type?.id?.toString() || ""
  );
  const [extraFields, setExtraFields] = useState<AnnouncementExtraFields[]>(() => {
    const persistedExtraFields = editingAnnouncement?.extra_fields;
    if (persistedExtraFields && persistedExtraFields.length > 0) {
      return [...persistedExtraFields];
    }
    return [...(editingAnnouncement?.announcement_type?.extra_fields ?? [])];
  });
  const [dateRangeOption, setDateRangeOption] = useState("7-days");
  const [startDate, setStartDate] = useState<Date | undefined>(
    editingAnnouncement?.scheduled_start_date ? new Date(editingAnnouncement.scheduled_start_date) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    editingAnnouncement?.scheduled_end_date ? new Date(editingAnnouncement.scheduled_end_date) : undefined
  );
  const [startTime, setStartTime] = useState("12:00");
  const [endTime, setEndTime] = useState("12:00");

  // Media upload state
  const [mediaUrl, setMediaUrl] = useState<string | undefined>(
    editingAnnouncement?.media_url ? stripQueryParams(editingAnnouncement.media_url) : undefined,
  );
  const [mediaType, setMediaType] = useState<string | undefined>(editingAnnouncement?.media_type ?? undefined);
  const [mediaSizeKb, setMediaSizeKb] = useState<number | undefined>(editingAnnouncement?.media_size_kb ?? undefined);
  const [uploadedFileName, setUploadedFileName] = useState<string | undefined>(
    editingAnnouncement?.media_url ? extractFilename(editingAnnouncement.media_url) : undefined,
  );
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { mutateAsync: generateSignedUrl } = useSecureAnnouncementUploadApiService();

  const validateFileSize = (file: File): string | null => {
    if (ANNOUNCEMENT_IMAGE_FILE_EXTENSION.includes(file.type)) {
      if (file.size > ANNOUNCEMENT_ACCEPT_IMAGE_FILE_SIZE) {
        return "Image file size exceeds the limit of 5MB";
      }
    } else if (file.size > ANNOUNCEMENT_ACCEPT_FILE_SIZE) {
      return "File size exceeds the limit of 16MB";
    }
    return null;
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const { currentTarget } = event;
    const { files } = currentTarget;
    if (!files || files.length === 0) return;

    const file = files[0];
    const sizeError = validateFileSize(file);
    if (sizeError) {
      setUploadError(sizeError);
      currentTarget.value = "";
      return;
    }

    setUploadError(null);
    setUploading(true);

    try {
      const fileName = file.name.replace(/\.[^/.]+$/, "");
      const fileExtension = getFileExtension(file.name).toLowerCase();

      const response = await generateSignedUrl({
        file_name: fileName,
        file_extension: fileExtension,
        upload_type: "announcement",
      });

      const unsignedAxios = axios.create();
      await unsignedAxios.put(response.signed_url, file, {
        headers: {
          "Content-Type": response.content_type,
          "Content-Length": file.size.toString(),
          "X-Goog-Content-Length-Range": `${response.min_size},${response.max_size}`,
        },
      });

      setMediaUrl(response.file_path);
      setMediaType(getMediaType(file.type));
      setMediaSizeKb(Math.round(file.size / 1024));
      setUploadedFileName(file.name);
    } catch (error) {
      const msg =
        axios.isAxiosError(error)
          ? error.response?.data?.error || error.response?.data?.message || "Upload failed"
          : "Upload failed";
      setUploadError(msg);
    } finally {
      setUploading(false);
      currentTarget.value = "";
    }
  };

  const handleRemoveMedia = (): void => {
    setMediaUrl(undefined);
    setMediaType(undefined);
    setMediaSizeKb(undefined);
    setUploadedFileName(undefined);
    setUploadError(null);
  };

  const estimateAudienceMutation = useEstimateAudienceApiService();
  const estimateMutate = estimateAudienceMutation.mutate;
  const debounceRef = useRef<NodeJS.Timeout>();

  const hasAffiliatedCompanies = selectedCompanyIds.some((id) => id !== ownerCompanyId);

  const estimateAudience = useCallback(() => {
    if (!selectedCompanyIds.some((id) => id !== ownerCompanyId)) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      estimateMutate({
        target_employer_ids: selectedCompanyIds.filter((id) => id !== ownerCompanyId),
        segment_ids: selectedSegmentIds.length > 0 ? selectedSegmentIds : undefined,
        owner_segment_ids: selectedOwnerSegmentIds.length > 0 ? selectedOwnerSegmentIds : undefined,
      });
    }, 500);
  }, [selectedCompanyIds, selectedSegmentIds, selectedOwnerSegmentIds, estimateMutate]);

  useEffect(() => {
    estimateAudience();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [estimateAudience]);

  const combineDateAndTime = (date: Date, time: string): Date => {
    const parts = time.split(":");
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const combined = new Date(date);
    combined.setHours(hours, minutes, 0, 0);
    return combined;
  };

  const buildRequest = (): CreateMcmAnnouncementRequest => {
    const request: CreateMcmAnnouncementRequest = {
      title,
      content,
      target_employer_ids: selectedCompanyIds.filter((id) => id !== ownerCompanyId),
    };
    if (announcementTypeId) {
      request.announcement_type_id = Number(announcementTypeId);
      request.extra_fields = extraFields;
    }
    if (selectedSegmentIds.length > 0) {
      request.segment_ids = selectedSegmentIds;
    }
    if (selectedOwnerSegmentIds.length > 0) {
      request.owner_segment_ids = selectedOwnerSegmentIds;
    }
    if (mediaUrl) {
      request.media_url = mediaUrl;
      request.media_type = mediaType;
      request.media_size_kb = mediaSizeKb;
    }
    if (dateRangeOption === "custom") {
      if (startDate) {
        request.scheduled_start_date = combineDateAndTime(startDate, startTime).toISOString();
      }
      if (endDate) {
        request.scheduled_end_date = combineDateAndTime(endDate, endTime).toISOString();
      }
    } else {
      const now = new Date();
      const presetDays: Record<string, number> = { "24h": 1, "3-days": 3, "7-days": 7 };
      const days = presetDays[dateRangeOption];
      if (days) {
        const end = new Date(now);
        end.setDate(end.getDate() + days);
        request.scheduled_start_date = now.toISOString();
        request.scheduled_end_date = end.toISOString();
      }
    }
    return request;
  };

  const estimatedAudience = estimateAudienceMutation.data || null;
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = "Please complete this field";
    if (!content.trim()) newErrors.content = "Please complete this field";
    if (!selectedCompanyIds.some((id) => id !== ownerCompanyId))
      newErrors.companies = "Select at least one affiliated company";
    if (
      ownerCompanyId != null &&
      selectedCompanyIds.includes(ownerCompanyId) &&
      selectedOwnerSegmentIds.length === 0
    )
      newErrors.ownerSegments = "Select at least one segment to target your own employees";
    if (dateRangeOption === "custom") {
      if (!startDate) newErrors.startDate = "Start date is required";
      if (!endDate) newErrors.endDate = "End date is required";
      if (startDate && endDate) {
        const start = combineDateAndTime(startDate, startTime);
        const end = combineDateAndTime(endDate, endTime);
        if (end <= start) newErrors.endDate = "End date/time must be after the start";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveDraft = (): void => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = "Please complete this field";
    if (!content.trim()) newErrors.content = "Please complete this field";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    onSaveDraft(buildRequest());
  };

  const handlePublish = (): void => {
    if (!validate()) return;
    onPublish(buildRequest());
  };

  return (
    <div>
      <div className="max-w-[850px]">
        <p className="text-base font-semibold text-greyscale-text-title mb-md">
          {editingAnnouncement ? "Edit draft announcement" : "Compose cross-company announcement"}
        </p>

        <div className="flex flex-col gap-md">
          {/* Select a template */}
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-xxs">
              <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                Select a template
              </p>
              <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                Choose a message template to get started
              </p>
            </div>
            <Select
              value={announcementTypeId}
              onValueChange={(value) => {
                setAnnouncementTypeId(value);
                const selectedType = announcementTypes.find(
                  (t) => t.id.toString() === value,
                );
                setExtraFields(selectedType?.extra_fields || []);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select template" />
              </SelectTrigger>
              <SelectContent>
                {announcementTypes
                  .filter((t) =>
                    t.extra_fields.every((f) => f.type === "model"),
                  )
                  .map((t) => (
                    <SelectItem key={t.id} value={t.id.toString()}>
                      {t.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-xxs">
              <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                Title
              </p>
              <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                Give your announcement a clear, descriptive title.
              </p>
            </div>
            <InputField
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setTitle(e.target.value); setErrors((prev) => { const next = { ...prev }; delete next.title; return next; }); }}
              placeholder="e.g. Q1 Operational Update"
            />
            {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
          </div>

          {/* Message */}
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-xxs">
              <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                Message
              </p>
              <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                Write the announcement body. This will be seen by all
                employees in the selected companies.
              </p>
            </div>
            <Textarea
              value={content}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setContent(e.target.value); setErrors((prev) => { const next = { ...prev }; delete next.content; return next; }); }}
              placeholder="Write your announcement here..."
              className="min-h-[115px]"
            />
            {errors.content && <p className="text-xs text-red-500">{errors.content}</p>}
          </div>

          {/* Attach media */}
          <div className="flex flex-col gap-xxs">
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
              accept={ACCEPT_EXTENSIONS}
              disabled={uploading}
            />
            {uploadedFileName ? (
              <div className="flex items-center gap-xxs">
                <Paperclip size={14} className="text-greyscale-text-caption shrink-0" />
                <span className="text-sm text-greyscale-text-body truncate max-w-[300px]">
                  {uploadedFileName}
                </span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md h-6 w-6 text-greyscale-text-caption hover:text-red-500 hover:bg-greyscale-bg-subtle transition-colors shrink-0"
                  onClick={handleRemoveMedia}
                  aria-label="Remove attached file"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <Button
                leftIcon={<Paperclip size={11} />}
                className="w-fit"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploading ? "Uploading..." : "Attach Media"}
              </Button>
            )}
            {uploadError && (
              <p className="text-xs text-red-500">{uploadError}</p>
            )}
            {!uploadedFileName && !uploading && (
              <p className="text-xs text-greyscale-text-caption">
                Supports PDF, images (JPG, PNG), video (MP4), and audio (MP3, OGG, AMR). Max 5MB for images, 16MB for other files.
              </p>
            )}
          </div>

          {/* Date range */}
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-xxs">
              <p className="text-sm font-semibold leading-18 text-greyscale-text-title">
                Visibility window
              </p>
              <p className="text-sm font-regular leading-18 text-greyscale-text-body">
                How long should this announcement be visible to employees?
              </p>
            </div>
            <RadioGroup
              value={dateRangeOption}
              onValueChange={setDateRangeOption}
              className="flex gap-xs"
            >
              <div className="flex items-center gap-xxs">
                <RadioGroupItem value="24h" id="24h" className="h-4 w-4 shrink-0 flex items-center justify-center [&_svg]:h-2 [&_svg]:w-2" />
                <Label htmlFor="24h" className="text-sm">24h</Label>
              </div>
              <div className="flex items-center gap-xxs">
                <RadioGroupItem value="3-days" id="3-days" className="h-4 w-4 shrink-0 flex items-center justify-center [&_svg]:h-2 [&_svg]:w-2" />
                <Label htmlFor="3-days" className="text-sm">3 days</Label>
              </div>
              <div className="flex items-center gap-xxs">
                <RadioGroupItem value="7-days" id="7-days" className="h-4 w-4 shrink-0 flex items-center justify-center [&_svg]:h-2 [&_svg]:w-2" />
                <Label htmlFor="7-days" className="text-sm">7 days</Label>
              </div>
              <div className="flex items-center gap-xxs">
                <RadioGroupItem value="custom" id="custom" className="h-4 w-4 shrink-0 flex items-center justify-center [&_svg]:h-2 [&_svg]:w-2" />
                <Label htmlFor="custom" className="text-sm">Custom</Label>
              </div>
            </RadioGroup>

            {dateRangeOption === "custom" && (
              <div className="grid grid-cols-2 gap-md mt-xs">
                <div className="flex flex-col gap-xxxs">
                  <Label className="text-sm font-semibold text-greyscale-text-title">
                    Start Date <span className="text-red-500">*</span>
                  </Label>
                  <DatePicker
                    date={startDate}
                    onDateChange={(d) => { setStartDate(d); setErrors((prev) => { const next = { ...prev }; delete next.startDate; return next; }); }}
                    placeholder="Select start date"
                  />
                  {errors.startDate && <p className="text-xs text-red-500 mt-xxxs">{errors.startDate}</p>}
                </div>
                <div className="flex flex-col gap-xxxs">
                  <Label className="text-sm font-semibold text-greyscale-text-title">
                    Start Time
                  </Label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="h-10 rounded-md border border-greyscale-border px-md text-sm text-greyscale-text-title outline-none focus:ring-2 focus:ring-offset-2"
                  />
                </div>
                <div className="flex flex-col gap-xxxs">
                  <Label className="text-sm font-semibold text-greyscale-text-title">
                    End Date <span className="text-red-500">*</span>
                  </Label>
                  <DatePicker
                    date={endDate}
                    onDateChange={(d) => { setEndDate(d); setErrors((prev) => { const next = { ...prev }; delete next.endDate; return next; }); }}
                    placeholder="Select end date"
                  />
                  {errors.endDate && <p className="text-xs text-red-500 mt-xxxs">{errors.endDate}</p>}
                </div>
                <div className="flex flex-col gap-xxxs">
                  <Label className="text-sm font-semibold text-greyscale-text-title">
                    End Time
                  </Label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="h-10 rounded-md border border-greyscale-border px-md text-sm text-greyscale-text-title outline-none focus:ring-2 focus:ring-offset-2"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Select companies & segments */}
          <CompanySegmentPicker
            companies={companies}
            segments={segments}
            ownerCompanyId={ownerCompanyId}
            ownerCompanyName={ownerCompanyName}
            ownerSegments={ownerSegments}
            selectedCompanyIds={selectedCompanyIds}
            selectedSegmentIds={selectedSegmentIds}
            selectedOwnerSegmentIds={selectedOwnerSegmentIds}
            estimatedAudience={estimatedAudience}
            isEstimating={estimateAudienceMutation.isLoading}
            onSelectedCompanyIdsChange={(ids) => { setSelectedCompanyIds(ids); setErrors((prev) => { const next = { ...prev }; delete next.companies; return next; }); }}
            onSelectedSegmentIdsChange={setSelectedSegmentIds}
            onSelectedOwnerSegmentIdsChange={(ids) => { setSelectedOwnerSegmentIds(ids); setErrors((prev) => { const next = { ...prev }; delete next.ownerSegments; return next; }); }}
          />
          {errors.companies && <p className="text-xs text-red-500">{errors.companies}</p>}
          {errors.ownerSegments && <p className="text-xs text-red-500">{errors.ownerSegments}</p>}
        </div>
      </div>

      {/* Footer buttons */}
      <div className="border-t border-greyscale-border mt-lg" />
      <div className="flex items-center justify-between py-lg">
        <Button variant="outline" onClick={onGoBack}>
          Go Back
        </Button>
        <div className="flex items-center gap-xxs">
          {editingAnnouncement && getDisplayStatus(editingAnnouncement) === "live" && (
            <Button
              variant="outline"
              className="text-red-500 border-red-500 hover:bg-red-50"
              onClick={onCloseAnnouncement}
            >
              Close Announcement
            </Button>
          )}
          <Button variant="outline" disabled={uploading || isSaving || isPublishing} onClick={handleSaveDraft}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {uploading ? "Uploading..." : isSaving ? "Saving..." : "Save draft"}
          </Button>
          <Button
            leftIcon={<Send size={14} />}
            disabled={!hasAffiliatedCompanies || !title.trim() || uploading || isSaving || isPublishing}
            onClick={handlePublish}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {uploading ? "Uploading..." : isSaving || isPublishing ? "Sending..." : (
              <>
                {dateRangeOption === "custom" && startDate &&
                  combineDateAndTime(startDate, startTime) > new Date()
                  ? "Schedule" : "Send to"}{" "}
                {estimatedAudience?.total ?? 0} employees across{" "}
                {selectedCompanyIds.length}{" "}
                {selectedCompanyIds.length === 1 ? "company" : "companies"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComposeForm;
